import React, { useEffect, useMemo, useState } from "react";
import { HiPlus } from "react-icons/hi2";
import { IoCloseOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import {
  ITaxRate,
  useAddTaxRateMutation,
  useUpdateTaxRateMutation,
} from "../../../../services/BillingService";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { serverMessage } from "../../../../utils/serverMessage";
import { taxCodeProblem } from "./taxCode.utils";

type Props = {
  setIsOpenAdd: React.Dispatch<React.SetStateAction<boolean>>;
  /** Edit mode (owner 2026-10-22): name, rates and active of an existing code. */
  taxCode?: ITaxRate | null;
};

type TaxRateRow = {
  id: string;
  taxName: string;
  taxRate: string;
};

const createEmptyRateRow = (): TaxRateRow => ({
  id: uuidv4(),
  taxName: "",
  taxRate: "",
});

const formatRateForChip = (value: string) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return "0%";

  return `${parsed % 1 === 0 ? parsed.toFixed(0) : parsed.toFixed(2)}%`;
};

export const AddTaxCode: React.FC<Props> = ({ setIsOpenAdd, taxCode }) => {
  const isEdit = !!taxCode?.id;
  const [taxCodeName, setTaxCodeName] = useState(taxCode?.taxCodeName || "");
  const [isActive, setIsActive] = useState(taxCode?.isActive ?? true);
  const [taxRateData, setTaxRateData] = useState<TaxRateRow[]>(
    taxCode?.taxRates?.length
      ? taxCode.taxRates.map((rate) => ({
          id: uuidv4(),
          taxName: rate.taxName || "",
          taxRate: String(rate.taxRate ?? ""),
        }))
      : [createEmptyRateRow()],
  );

  const [addTaxCode, { isLoading: isAdding, isSuccess, isError }] =
    useAddTaxRateMutation();
  const [updateTaxCode, { isLoading: isUpdating }] = useUpdateTaxRateMutation();
  const isLoading = isAdding || isUpdating;

  useEffect(() => {
    if (isSuccess) {
      toast("Tax code successfully created", {
        type: "success",
      });
      setIsOpenAdd(false);
    }
  }, [isSuccess, setIsOpenAdd]);

  useEffect(() => {
    if (isError) {
      toast("Something went wrong", {
        type: "error",
      });
    }
  }, [isError]);

  const validTaxRates = useMemo(() => {
    return taxRateData.filter(
      (item) => item.taxName.trim() && item.taxRate.trim(),
    );
  }, [taxRateData]);

  const combinedRate = useMemo(() => {
    return validTaxRates.reduce(
      (sum, item) => sum + (parseFloat(item.taxRate) || 0),
      0,
    );
  }, [validTaxRates]);

  const problem = taxCodeProblem(
    taxCodeName,
    validTaxRates.map((item) => parseFloat(item.taxRate)),
  );
  const isSaveDisabled = !!problem;

  const closeModal = () => setIsOpenAdd(false);

  const handleAddTaxRate = () => {
    setTaxRateData((prev) => [...prev, createEmptyRateRow()]);
  };

  const handleRemoveTaxRate = (id: string) => {
    setTaxRateData((prev) => {
      if (prev.length === 1) {
        return [createEmptyRateRow()];
      }

      return prev.filter((item) => item.id !== id);
    });
  };

  const handleTaxNameChange = (id: string, value: string) => {
    setTaxRateData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, taxName: value } : item)),
    );
  };

  const handleTaxRateChange = (id: string, value: string) => {
    const normalized = value
      .replace(",", ".")
      .replace(/[^0-9.]/g, "")
      .replace(/^(\d*\.?\d{0,2}).*$/, "$1");

    setTaxRateData((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, taxRate: normalized } : item,
      ),
    );
  };

  const handleSubmit = async () => {
    if (isSaveDisabled) return;

    if (isEdit && taxCode?.id) {
      try {
        const result = await updateTaxCode({
          id: taxCode.id,
          orgId: getLinkedFirm().orgId,
          taxCodeName: taxCodeName.trim(),
          isActive,
          taxRates: validTaxRates.map((item) => ({
            taxName: item.taxName.trim(),
            taxRate: parseFloat(item.taxRate),
          })),
        }).unwrap();
        toast(
          result.newVersion
            ? "Saved as a new tax code. Invoices that already use the old rates keep them; the old code is now switched off."
            : "Tax code updated",
          { type: "success" },
        );
        setIsOpenAdd(false);
      } catch (error) {
        toast(serverMessage(error, "Couldn't save the tax code."), {
          type: "error",
        });
      }
      return;
    }

    await addTaxCode({
      orgId: getLinkedFirm().orgId,
      isActive: true,
      taxCodeName: taxCodeName.trim(),
      taxRates: validTaxRates.map((item) => ({
        taxName: item.taxName.trim(),
        taxRate: parseFloat(item.taxRate),
      })),
    });
  };

  return (
    <div
      className={
        "flex max-h-[90vh] w-full max-w-[512px] flex-col overflow-hidden rounded-[12px] bg-white font-Manrope"
      }>
      <div
        className={
          "flex shrink-0 items-center justify-between border-b border-[#E5E7EB] px-6 py-2"
        }>
        <div className={"text-base font-semibold text-[#1F2937]"}>
          {isEdit ? "Edit Tax Code" : "Create Tax Code"}
        </div>

        <button
          type={"button"}
          className={"cursor-pointer text-[#98A2B3]"}
          onClick={closeModal}>
          <IoCloseOutline size={24} />
        </button>
      </div>

      <div
        className={"overflow-y-auto py-5"}
        style={{ maxHeight: "calc(90vh - 145px)" }}>
        <div className={"mb-5 px-6"}>
          <label className={"label px-0"}>
            <span className={"label-text"}>Tax Code Name</span>
          </label>
          <input
            type={"text"}
            value={taxCodeName}
            onChange={(e) => setTaxCodeName(e.target.value)}
            placeholder={"Type tax code name..."}
            className={"input input-bordered h-8 w-full text-xs"}
          />
        </div>

        {isEdit && (
          <div className={"mb-5 px-6"}>
            <label
              className={
                "flex cursor-pointer items-center gap-2 text-sm text-[#344054]"
              }>
              <input
                type={"checkbox"}
                className={"checkbox checkbox-sm"}
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active (can be picked on new invoices)
            </label>
            <div
              className={
                "mt-3 rounded-lg bg-[#F4F2FF] px-3 py-2 text-xs text-[#5B3FE6]"
              }>
              Invoices already using this tax code keep their amounts. If you
              change its rates and invoices use it, a new tax code is saved and
              the old one is switched off.
            </div>
          </div>
        )}

        <div className={"mb-4 px-6"}>
          <div className={"text-sm font-medium text-[#344054]"}>Tax Rates</div>
          <div className={"mt-1 text-sm text-[#98A2B3]"}>
            Add one or more tax rates below
          </div>
        </div>

        <div className={"space-y-3 px-6"}>
          {taxRateData.map((item) => (
            <div
              key={item.id}
              className={
                "flex items-center gap-3 rounded-[12px] border border-[#EAECF0] bg-[#F9FAFB] p-3"
              }>
              <input
                type={"text"}
                value={item.taxName}
                onChange={(e) => handleTaxNameChange(item.id, e.target.value)}
                placeholder={"Type tax rate name..."}
                className={"input input-bordered h-8 w-full text-xs"}
              />

              <div className={"relative w-[132px]"}>
                <input
                  type={"text"}
                  inputMode={"decimal"}
                  value={item.taxRate}
                  onChange={(e) => handleTaxRateChange(item.id, e.target.value)}
                  placeholder={"0.00"}
                  className={"input input-bordered h-8 w-full text-xs"}
                />
                <span
                  className={
                    "absolute right-4 top-1/2 -translate-y-1/2 text-[16px] font-semibold text-[#98A2B3]"
                  }>
                  %
                </span>
              </div>

              <button
                type={"button"}
                onClick={() => handleRemoveTaxRate(item.id)}
                className={"text-[#EF4444]"}>
                <IoCloseOutline size={24} />
              </button>
            </div>
          ))}
        </div>

        <div className={"px-6"}>
          <button
            type={"button"}
            onClick={handleAddTaxRate}
            className={
              "mt-4 flex h-8 w-full items-center justify-center gap-2 rounded-[10px] border border-[#D0D5DD] bg-white text-sm font-semibold text-[#1F2937] transition hover:bg-[#F9FAFB]"
            }>
            <HiPlus size={18} />
            Add Tax Rate
          </button>
        </div>

        <div className={"mt-5 border-t border-[#E5E7EB] px-6 pt-4"}>
          <div className={"mb-3 text-base font-medium text-[#1F2937]"}>
            Preview
          </div>

          <div className={"flex items-center justify-between gap-4"}>
            <div className={"flex flex-wrap gap-2"}>
              {validTaxRates.length > 0 ? (
                validTaxRates.map((item) => (
                  <div
                    key={item.id}
                    className={
                      "rounded-full bg-[#F2F4F7] px-4 py-2 text-sm font-medium text-[#475467]"
                    }>
                    {item.taxName} {formatRateForChip(item.taxRate)}
                  </div>
                ))
              ) : (
                <div
                  className={"h-[40px] w-[96px] rounded-full bg-[#F2F4F7]"}
                />
              )}
            </div>

            <div className={"text-right text-base text-[#344054]"}>
              <span className={"mr-2"}>Combined Rate :</span>
              <span className={"font-semibold text-[#101828]"}>
                {combinedRate.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        className={
          "flex shrink-0 items-center justify-end gap-3 border-t border-[#E5E7EB] px-6 py-4 text-sm"
        }>
        {problem && taxCodeName.trim() && validTaxRates.length > 0 && (
          <span className={"mr-auto text-xs text-[#E5484D]"}>{problem}</span>
        )}
        <button
          type={"button"}
          onClick={closeModal}
          disabled={isLoading}
          className={
            "flex h-[30px] w-[100px] items-center justify-center rounded-lg border border-[#C8D2DE] px-4 py-2 text-[#253747] disabled:opacity-75"
          }>
          Cancel
        </button>

        <button
          type={"button"}
          onClick={handleSubmit}
          disabled={isSaveDisabled || isLoading}
          className={
            "flex h-[30px] w-[100px] items-center justify-center rounded-lg bg-[#7c66f0] px-4 py-2 text-white"
          }>
          {isLoading ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};
